import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import UserProfileModal from '../Profile/UserProfileModal';
import './Navbar.css';

const Navbar: React.FC = () => {
  const { user, logout } = useAuth();
  const { daysRemaining, openModal, isActive } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSubMenu, setActiveSubMenu] = useState<string | null>(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  const isPhoneMissing = !!user && !user.phone;

  const navItems = [
    { label: 'Ana Sayfa', path: '/dashboard', icon: '🏠' },
    {
      label: 'PLANLAMA',
      path: '/planning',
      icon: '📅',
      color: '#39ff14'
    },
    {
      label: 'İstatistik',
      path: '/statistics',
      icon: '📊',
      color: '#00e5ff'
    },
    {
      label: 'Arşiv Yönetimi',
      path: '/archive',
      icon: '📑',
      color: '#ff00ff'
    },
    {
      label: 'Belge Yönetimi',
      path: '/documents',
      icon: '📂',
      color: '#ffcf00',
      subItems: [
        { label: 'Risk Analizi', path: '/documents/risk', icon: '⚠️' },
        { label: 'Yıllık Çalışma Planı', path: '/documents/annual-work', icon: '📅' },
        { label: 'Yıllık Eğitim Planı', path: '/documents/annual-training', icon: '🎓' },
        { label: 'Acil Eylem Planı', path: '/documents/emergency', icon: '🚨' },
        { label: 'Tahliye Krokisi', path: '/documents/evacuation-plan', icon: '🗺️' },
        { label: 'Sertifikalar', path: '/documents/certificates', icon: '📄' }
      ]
    }
  ];

  const handleNavigate = (path: string) => {
    navigate(path);
    setActiveSubMenu(null);
  };

  // PROJE_yeni_hali'ndeki rozet renk kuralı:
  const getBadgeColor = () => {
    if (daysRemaining === null || !isActive) return '#ff4757'; // Red
    if (daysRemaining > 30) return '#2ed573'; // Green
    if (daysRemaining > 7) return '#ffa502'; // Orange
    return '#ff4757'; // Red
  };

  const isBlinking = !isActive || daysRemaining <= 7;
  const badgeText = daysRemaining > 36500 ? 'Sınırsız' : (!isActive || daysRemaining <= 0 ? 'Süre Doldu - Yenile' : `Kalan: ${daysRemaining} Gün`);

  return (
    <nav className="web-field-navbar">
      <div className="nav-container">
        {/* Sol: Logo */}
        <div className="nav-left">
          <div className="nav-logo" onClick={() => handleNavigate('/dashboard')}>
            <div className="logo-text">
              <div className="logo-main-wrapper">
                <span className="logo-main">UZMAN DESTEK</span>
                <a
                  href="https://wa.me/905072136474"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whatsapp-support-btn"
                  title="WhatsApp Teknik Yardım: 0507 213 64 74"
                  onClick={(e) => e.stopPropagation()}
                >
                  <i className="fab fa-whatsapp" style={{ fontSize: '13px' }}></i>
                  <span>TEKNİK YARDIM</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Orta: Nav Linkleri */}
        <div className="nav-center">
          <ul className="nav-links">
            {navItems.map((item) => (
              <li
                key={item.label}
                className={`nav-item-wrapper ${location.pathname === item.path ? 'active' : ''}`}
                onMouseEnter={() => setActiveSubMenu(item.label)}
                onMouseLeave={() => setActiveSubMenu(null)}
              >
                <button
                  onClick={() => handleNavigate(item.path)}
                  className="nav-button"
                  style={{ '--hover-color': item.color || '#ffffff' } as any}
                >
                  <span className="nav-icon">{item.icon}</span>
                  <span className="nav-label">{item.label}</span>
                  {item.subItems && <span className="drop-indicator">▾</span>}
                </button>

                {item.subItems && activeSubMenu === item.label && (
                  <div className="vertical-dropdown glass-morphism">
                    {item.subItems.map((sub) => (
                      <button
                        key={sub.label}
                        onClick={() => handleNavigate(sub.path)}
                        className="dropdown-item"
                      >
                        <span className="sub-icon">{sub.icon}</span>
                        <span className="sub-label">{sub.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </li>
            ))}

            {/* Ayarlar Menüsü */}
            <li
              className="nav-item-wrapper"
              onMouseEnter={() => setActiveSubMenu('Ayarlar')}
              onMouseLeave={() => setActiveSubMenu(null)}
            >
              <button className="nav-button" style={{ '--hover-color': '#ff7f00' } as any}>
                <span className="nav-icon">⚙️</span>
                <span className="nav-label">Ayarlar</span>
                <span className="drop-indicator">▾</span>
              </button>

              {activeSubMenu === 'Ayarlar' && (
                <div className="vertical-dropdown glass-morphism">
                  <button
                    onClick={() => {
                      setIsProfileModalOpen(true);
                      setActiveSubMenu(null);
                    }}
                    className="dropdown-item"
                  >
                    <span>👤</span> Kişisel Bilgilerim
                  </button>
                  <button onClick={() => handleNavigate('/subscription')} className="dropdown-item">
                    <span>💳</span> Lisans & Paketler
                  </button>
                  <div className="menu-divider"></div>
                  <button onClick={logout} className="dropdown-item logout">
                    <span>🚪</span> Çıkış Yap
                  </button>
                </div>
              )}
            </li>
          </ul>
        </div>

        {/* Sağ: Kalan Gün Rozet Butonu ve Profil */}
        <div className="nav-right">
          {/* Kalan ... Gün Butonu (PROJE_yeni_hali stili) */}
          <button 
            className={`subscription-badge ${isBlinking ? 'blinking' : ''}`}
            onClick={() => handleNavigate('/subscription')}
            style={{ backgroundColor: getBadgeColor(), cursor: 'pointer' }}
            title="Abonelik durumunu yönetmek ve süreyi yenilemek için tıklayın"
          >
            {badgeText}
          </button>

          {/* Profil */}
          <div
            className="user-profile-summary"
            title={user?.fullName || ''}
            onClick={() => setIsProfileModalOpen(true)}
            style={{ cursor: 'pointer' }}
          >
            <div className="user-text">
              <span className="user-name-prominent">{user?.fullName || 'Kullanıcı'}</span>
            </div>
            <div className="user-avatar">
              👷
            </div>
          </div>
        </div>
      </div>

      {/* Kişisel Bilgiler ve Güvenlik Modalı (Veya Zorunlu Telefon Tamamlama) */}
      <UserProfileModal
        isOpen={isProfileModalOpen || isPhoneMissing}
        onClose={() => setIsProfileModalOpen(false)}
        isForcedPhone={isPhoneMissing}
      />
    </nav>
  );
};

export default Navbar;
